import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DONE_STEP_ID,
  fetchEmploymentContractProgress,
  upsertEmploymentContractProgress,
} from './employmentContractApi';
import type {
  EmploymentContractCommit,
  EmploymentContractFormValues,
  EmploymentContractStepId,
} from './employmentContractTypes';
import { FORM_CODE_FF002 } from './employmentContractTypes';

type TFunc = (key: string) => string;

const DEFAULT_MESSAGES_PREFIX = 'submitDocuments.employmentContract';
const STEPS: EmploymentContractStepId[] = ['preview', 'agree', 'signedPreview'];

function rebuildCommitted(
  values: EmploymentContractFormValues,
  msg: (suffix: string) => string
): EmploymentContractCommit[] {
  const out: EmploymentContractCommit[] = [];
  if (values.documentRead === 'yes') {
    out.push({
      stepId: 'preview',
      label: msg('steps.preview'),
      displayValue: msg('previewDone'),
    });
  }
  if (values.agree === 'true') {
    out.push({
      stepId: 'agree',
      label: msg('steps.agree'),
      displayValue: msg('yes'),
    });
  } else if (values.agree === 'false') {
    out.push({
      stepId: 'agree',
      label: msg('steps.agree'),
      displayValue: msg('no'),
    });
  }
  return out;
}

function nextStepFromValues(values: EmploymentContractFormValues): EmploymentContractStepId | null {
  if (values.documentRead !== 'yes') return 'preview';
  if (values.agree !== 'true') return 'agree';
  return 'signedPreview';
}

export function useEmploymentContractFlow(
  currentEmail: string | null | undefined,
  invitationId: string | null | undefined,
  t: TFunc,
  formCode: string = FORM_CODE_FF002,
  messagesPrefix: string = DEFAULT_MESSAGES_PREFIX
) {
  const [values, setValues] = useState<EmploymentContractFormValues>({});
  const [committed, setCommitted] = useState<EmploymentContractCommit[]>([]);
  const [currentStepId, setCurrentStepId] = useState<EmploymentContractStepId | null>('preview');
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingCommitIndex, setEditingCommitIndex] = useState<number | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const email = currentEmail?.trim() ?? '';
  const invId = invitationId?.trim() ?? '';
  const code = (formCode?.trim() || FORM_CODE_FF002).toUpperCase();
  const prefix = messagesPrefix?.trim() || DEFAULT_MESSAGES_PREFIX;
  const msg = useCallback((suffix: string) => t(`${prefix}.${suffix}`), [t, prefix]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setSaveError(null);
      const progress = await fetchEmploymentContractProgress(email, invId, code);
      if (cancelled) return;
      if (progress) {
        setValues(progress.values);
        setCommitted(rebuildCommitted(progress.values, msg));
        if (progress.currentStepId === DONE_STEP_ID) {
          setCurrentStepId(null);
        } else if (
          progress.currentStepId === 'preview' ||
          progress.currentStepId === 'agree' ||
          progress.currentStepId === 'signedPreview'
        ) {
          setCurrentStepId(progress.currentStepId);
        } else {
          setCurrentStepId(nextStepFromValues(progress.values));
        }
      } else {
        setValues({});
        setCommitted([]);
        setCurrentStepId('preview');
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [email, invId, code, msg]);

  const persist = useCallback(
    async (nextValues: EmploymentContractFormValues, stepId: string | null) => {
      setSaveError(null);
      const saved = await upsertEmploymentContractProgress({
        email,
        invitationId: invId,
        formCode: code,
        values: nextValues,
        currentStepId: stepId,
      });
      if (!saved) {
        setSaveError(msg('saveFailed'));
        return false;
      }
      setValues(saved.values);
      setCommitted(rebuildCommitted(saved.values, msg));
      if (saved.currentStepId === DONE_STEP_ID) {
        setCurrentStepId(null);
      } else if (
        saved.currentStepId === 'preview' ||
        saved.currentStepId === 'agree' ||
        saved.currentStepId === 'signedPreview'
      ) {
        setCurrentStepId(saved.currentStepId);
      } else {
        setCurrentStepId(nextStepFromValues(saved.values));
      }
      return true;
    },
    [email, invId, code, msg]
  );

  const markDocumentRead = useCallback(async (): Promise<boolean> => {
    const next = { ...values, documentRead: 'yes' as const };
    return persist(next, 'agree');
  }, [persist, values]);

  const advanceAgree = useCallback(
    async (choice: 'yes' | 'no') => {
      if (choice === 'no') {
        const next = { ...values, agree: 'false' as const };
        return persist(next, 'agree');
      }
      const next = { ...values, documentRead: 'yes' as const, agree: 'true' as const };
      return persist(next, 'signedPreview');
    },
    [persist, values]
  );

  const handleFinalComplete = useCallback(async (): Promise<boolean> => {
    const next = {
      ...values,
      documentRead: 'yes' as const,
      agree: 'true' as const,
    };
    return persist(next, DONE_STEP_ID);
  }, [persist, values]);

  const isComplete = currentStepId === null && values.agree === 'true' && values.documentRead === 'yes';
  const materialsDeclined = values.agree === 'false';
  const awaitingUnsignedPreview = currentStepId === 'preview' || values.documentRead !== 'yes';
  const awaitingAgree = values.documentRead === 'yes' && values.agree !== 'true' && values.agree !== 'false';
  const awaitingSignedPreview =
    values.agree === 'true' && (currentStepId === 'signedPreview' || currentStepId === null);

  const currentStep = useMemo(() => {
    if (!currentStepId || currentStepId === 'preview' || currentStepId === 'signedPreview') {
      return null;
    }
    if (currentStepId === 'agree') {
      return { stepId: 'agree' as const };
    }
    return null;
  }, [currentStepId]);

  return {
    t,
    msg,
    messagesPrefix: prefix,
    values,
    committed,
    currentStep,
    currentStepId,
    isLoading,
    saveError,
    editingCommitIndex,
    setEditingCommitIndex,
    threadEndRef,
    isComplete,
    materialsDeclined,
    awaitingUnsignedPreview,
    awaitingAgree,
    awaitingSignedPreview,
    markDocumentRead,
    advanceAgree,
    handleFinalComplete,
    steps: STEPS,
  };
}
